import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Copy, Share2, Users, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ShareButtons } from "@/components/posts/ShareButtons";

interface InviteData {
  id: string;
  invite_code: string;
  uses_count: number;
  max_uses: number;
  expires_at: string;
  status: string;
  created_at: string;
}

interface UserInviteStats {
  invites_sent: number;
  invite_limit: number;
}

export const InviteGenerator = () => {
  const [invites, setInvites] = useState<InviteData[]>([]);
  const [userStats, setUserStats] = useState<UserInviteStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchUserInvites();
    fetchUserStats();
  }, []);

  const fetchUserStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('invites_sent, invite_limit')
        .eq('id', user.id)
        .single();

      if (data) {
        setUserStats(data);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchUserInvites = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_invites')
        .select('*')
        .eq('inviter_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvites(data || []);
    } catch (error) {
      console.error('Error fetching invites:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się załadować zaproszeń",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateInviteCode = async () => {
    setGeneratingInvite(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('generate_invite_code', {
        inviter_user_id: user.id,
        max_invite_uses: 1
      });

      if (error) throw error;

      toast({
        title: "Sukces!",
        description: `Wygenerowano kod zaproszenia: ${data}`,
      });

      // Refresh data
      fetchUserInvites();
      fetchUserStats();
    } catch (error: any) {
      console.error('Error generating invite:', error);
      toast({
        title: "Błąd",
        description: error.message || "Nie udało się wygenerować zaproszenia",
        variant: "destructive",
      });
    } finally {
      setGeneratingInvite(false);
    }
  };

  const copyToClipboard = async (code: string) => {
    const inviteUrl = `${window.location.origin}/?invite=${code}`;
    await navigator.clipboard.writeText(inviteUrl);
    toast({
      title: "Skopiowano!",
      description: "Link zaproszenia został skopiowany do schowka",
    });
  };

  const getInviteUrl = (code: string) => `${window.location.origin}/?invite=${code}`;

  const canCreateInvite = userStats && userStats.invites_sent < userStats.invite_limit;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="w-5 h-5" />
          Zaproś znajomych
        </CardTitle>
        <CardDescription>
          Wygeneruj kody zaproszenia i udostępnij je znajomym
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {userStats && (
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">Wysłane zaproszenia:</span>
            </div>
            <span className="font-medium">
              {userStats.invites_sent} / {userStats.invite_limit}
            </span>
          </div>
        )}

        <Button 
          onClick={generateInviteCode}
          disabled={generatingInvite || !canCreateInvite}
          className="w-full"
        >
          {generatingInvite ? "Generowanie..." : "Wygeneruj nowe zaproszenie"}
        </Button>

        {!canCreateInvite && userStats && (
          <p className="text-sm text-muted-foreground text-center">
            Osiągnięto limit zaproszeń ({userStats.invite_limit})
          </p>
        )}

        {/* Active Invites */}
        <div className="space-y-3">
          <h4 className="font-medium">Twoje zaproszenia</h4>
          {loading ? (
            <p className="text-sm text-muted-foreground">Ładowanie...</p>
          ) : invites.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak aktywnych zaproszeń</p>
          ) : (
            invites.map((invite) => (
              <div key={invite.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                    {invite.invite_code}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    invite.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {invite.status === 'pending' ? 'Aktywne' : 'Użyte'}
                  </span>
                </div>
                
                <div className="text-xs text-muted-foreground flex justify-between">
                  <span>Użycia: {invite.uses_count}/{invite.max_uses}</span>
                  <span>Wygasa: {new Date(invite.expires_at).toLocaleDateString()}</span>
                </div>

                {invite.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(invite.invite_code)}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Kopiuj link
                    </Button>
                    
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Share2 className="w-3 h-3 mr-1" />
                          Udostępnij
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Udostępnij zaproszenie</DialogTitle>
                          <DialogDescription>
                            Wyślij link zaproszenia znajomym przez różne platformy
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium">Link zaproszenia:</label>
                            <Input 
                              value={getInviteUrl(invite.invite_code)}
                              readOnly
                              className="mt-1"
                            />
                          </div>
                          <ShareButtons 
                            postId={invite.id}
                            content={`Dołącz do naszej społeczności! Użyj kodu: ${invite.invite_code} lub kliknij: ${getInviteUrl(invite.invite_code)}`}
                            isCompact={false}
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};